import { useTranslation } from 'react-i18next';
import type { TagResponse, ThematicTagExtractionResponse, TagExtractionResponse } from '@/types/api';
import styles from './ResultPanel.module.css';

interface ResultPanelProps {
  response: TagResponse | null;
  traceId: string | null;
  error: string | null;
  loading?: boolean;
}

function isThematicResponse(response: TagResponse): response is ThematicTagExtractionResponse {
  return response.type === 'thematic';
}

export function ResultPanel({ response, traceId, error, loading }: ResultPanelProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className={styles.panel}>
        <h3 className={styles.title}>{t('result.title')}</h3>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.panel}>
        <h3 className={styles.title}>{t('result.title')}</h3>
        <div className={styles.error}>
          <strong>{t('common.error')}:</strong> {error}
        </div>
        {traceId && (
          <div className={styles.traceId}>
            <strong>{t('result.traceId')}:</strong> <code>{traceId}</code>
          </div>
        )}
      </div>
    );
  }

  if (!response) {
    return null;
  }

  return (
    <div className={styles.panel}>
      <h3 className={styles.title}>{t('result.title')}</h3>
      
      {traceId && (
        <div className={styles.traceId}>
          <strong>{t('result.traceId')}:</strong> <code>{traceId}</code>
        </div>
      )}

      {isThematicResponse(response) ? (
        <div className={styles.results}>
          <h4 className={styles.subtitle}>{t('result.tags')} ({response.tags.length})</h4>
          {response.tags.length === 0 ? (
            <p className={styles.empty}>{t('result.noTags')}</p>
          ) : (
            <ul className={styles.tagList}>
              {response.tags.map((tag) => (
                <li key={tag.id} className={styles.tagItem}>
                  <div className={styles.tagHeader}>
                    <span className={styles.tagName}>{tag.name}</span>
                    {tag.similarity_score !== null && (
                      <span className={styles.tagScore}>
                        {t('result.similarity')}: {(tag.similarity_score * 100).toFixed(1)}%
                      </span>
                    )}
                  </div>
                  <p className={styles.tagDefinition}>{tag.definition}</p>
                  <span className={styles.tagDomain}>{t('result.domain')}: {tag.domain}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className={styles.results}>
          <h4 className={styles.subtitle}>{t('result.suggestions')} ({(response as TagExtractionResponse).suggestions.length})</h4>
          {(response as TagExtractionResponse).suggestions.length === 0 ? (
            <p className={styles.empty}>{t('result.noSuggestions')}</p>
          ) : (
            <ul className={styles.suggestionList}>
              {(response as TagExtractionResponse).suggestions.map((suggestion, idx) => (
                <li key={idx} className={styles.suggestionItem}>
                  <div className={styles.suggestionHeader}>
                    <span className={styles.suggestionValue}>{suggestion.value}</span>
                    {suggestion.confidence !== null && (
                      <span className={styles.suggestionConfidence}>
                        {t('result.confidence')}: {(suggestion.confidence * 100).toFixed(1)}%
                      </span>
                    )}
                  </div>
                  {suggestion.rationale && (
                    <p className={styles.suggestionRationale}>{suggestion.rationale}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
